import 'core-js/stable'
import 'regenerator-runtime/runtime'

import axios from 'axios'
axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN'
axios.defaults.withCredentials = true

import moment from 'moment'

const debug = process.env.NODE_ENV !== 'production'

let init = false

class Canceled {}
class RemoteError {
  constructor (ex) {
    this.message = `Remote Error (${ex.type}): ${JSON.stringify(ex.args)}`
    this.type = ex.type
    this.args = ex.args
  }
}

export default {
  async getUser(ref) {
    return this.__rpc(ref, { data: [ 'get_user' ] })
  },
  async getPermissions(ref) {
    return this.__rpc(ref, { data: [ 'get_permissions' ] })
  },
  async getUnits(ref) {
    let { uids } = ref
    let o = await this.__rpc(ref, { data: [ 'get_units', [ uids ] ] })
    o.until = moment.unix(o.until)
    for (const uid in o.units) {
      const u = o.units[uid]
      u.id = uid
      if (u.lastPacketTime)
        u.lastPacketTime = moment.unix(u.lastPacketTime)
      for (const coord of u.gps) {
        coord.time = moment.unix(coord.time)
      }
    }
    return o
  },
  async savePoi(ref) {
    let { poi, fleet } = ref,
      args = [ poi ]
    if (fleet)
      args.push(fleet.id)
    return await this.__rpc(ref, { data: [ 'save_poi', args ] })
  },
  async deletePoi(ref) {
    let { poi } = ref
    return await this.__rpc(ref, { data: [ 'delete_poi', [ poi.id ] ] })
  },
  async unitStaticData(ref) {
    let { uid, data } = ref,
      args = [ uid ]
    if (typeof(data) !== 'undefined')
      args.push(data)
    return await this.__rpc(ref, { data: [ 'unit_static_data', args ] })
  },
  async unitAdditionalData(ref) {
    let { uid } = ref
    return await this.__rpc(ref, { data: [ 'unit_additional_data', [ uid ] ] })
  },
  async unitTracklog(ref) {
    let { uid, start, end } = ref
    // this resultset can be HUGE and we don't need to convert unix times to moment instances
    // so let's leave them as is
    return await this.__rpc(ref, { data: [ 'fetch_tracklog', [ uid, start.unix(), end.unix() ] ] })
  },
  async unitHeartbeatTimestamps(ref) {
    let { uid, start, end } = ref
    // don't convert to moment instances; could be a lot of data here
    return await this.__rpc(ref, { data: [ 'fetch_heartbeat_timestamps', [ uid, start.unix(), end.unix() ] ] })
  },
  async unitStatus(ref) {
    let { uid, when, cutoff } = ref
    return await this.__rpc(ref, { data: [ 'fetch_status', [ uid, when.unix(), cutoff.unix() ] ] })
  },
  async unitFiles(ref) {
    let { uid, before, after } = ref,
      kwargs = {}
    if (typeof(before) !== 'undefined')
      kwargs.before = before.unix()
    if (typeof(after) !== 'undefined')
      kwargs.after = after.unix()
    let rv = await this.__rpc(ref, { data: [ 'list_files', [ uid ], kwargs ] })
    if (rv.next_run !== null) {
      rv.next_run = moment.unix(rv.next_run)
    }
    for (let f of rv.files) {
      f.acquired = moment.unix(f.acquired)
    }
    return rv
  },
  async triggerDownload(ref) {
    let { uid } = ref
    return await this.__rpc(ref, { data: [ 'trigger_file_download', [ uid ] ] })
  },
  async fleetUserList(ref) {
    let { fleet_id } = ref
    return await this.__rpc(ref, { data: [ 'fleet_user_list', [ fleet_id ] ] })
  },
  async fleetUserSet(ref) {
    let { fleet_id, user_data } = ref
    return await this.__rpc(ref, { data: [ 'fleet_user_set', [ fleet_id, user_data ] ] })
  },
  async fleetUserRevoke(ref) {
    let { fleet_id, user_email } = ref
    return await this.__rpc(ref, { data: [ 'fleet_user_revoke', [ fleet_id, user_email ] ] })
  },
  async rerunReport(ref) {
    let { rid } = ref
    return await this.__rpc(ref, { data: [ 'rerun_report', [ rid ] ] })
  },
  async rerenderReport(ref) {
    let { rid } = ref
    return await this.__rpc(ref, { data: [ 'rerender_report', [ rid ] ] })
  },
  async forceReport(ref) {
    let { rid } = ref
    return await this.__rpc(ref, { data: [ 'force_report', [ rid ] ] })
  },
  async updateFleet(ref) {
    let { fid, flds } = ref
    return await this.__rpc(ref, { data: [ 'update_fleet', [ fid, flds ] ] })
  },
  async sendMassEmail(ref) {
    let { subject, text, html } = ref
    return await this.__rpc(ref, { data: [ 'send_mass_email', [ subject, text, html ] ] })
  },
  async getAlerts(ref) {
    let args = {}
    for (const k of [ 'before', 'after', 'search', 'done', 'critical', 'limit', 'newest' ])
      if (k in ref)
        args[k] = ref[k]
    return await this.__rpc(ref, { data: [ 'get_alerts', args ] })
  },
  async archiveAlertsBulk(ref) {
    let args = { new_done: ref.newDone }
    for (const k of ['search', 'critical'])
      if (k in ref)
        args[k] = ref[k]
    return await this.__rpc(ref, { data: [ 'archive_alerts_bulk', args ] })
  },
  async updateAlerts(ref) {
    let { updates } = ref
    return await this.__rpc(ref, { data: [ 'update_alerts', [ updates ] ] })
  },
  async getAlertDetails(ref) {
    let { id } = ref
    return await this.__rpc(ref, { data: [ 'get_alert_details', [ id ] ] })
  },
  async getOwnerAccounts(ref) {
    return await this.__rpc(ref, { data: [ 'get_owner_accounts' ] })
  },
  async getOwnerUsers(ref) {
    return await this.__rpc(ref, { data: [ 'get_owner_users' ] })
  },
  async getAccess(ref) {
    let args = { }
    if ('fleetId' in ref) args.fleet_id = ref.fleetId
    if ('email' in ref) args.email = ref.email
    return await this.__rpc(ref, { data: [ 'get_access', args ] })
  },
  async setAccess(ref) {
    let args = {
      fleet_id: ref.fleetId,
      email: ref.email,
      flags: ref.flags,
      new_user: ref.newUser,
    }
    return await this.__rpc(ref, { data: [ 'set_access', args ] })
  },
  async sendInvitation(ref) {
    let { email } = ref
    return await this.__rpc(ref, { data: [ 'send_invitation', [ email ] ] })
  },
  async getConfirmation(ref) {
    let { purpose, code } = ref
    return await this.__rpc(ref, { data: [ 'get_confirmation', [ purpose, code ] ] })
  },
  async signUp(ref) {
    let { code, firstName, lastName, password } = ref
    return await this.__rpc(ref, { data: [ 'sign_up', [ code, firstName, lastName, password ] ] })
  },
  async requestPasswordReset(ref) {
    let { email } = ref
    return await this.__rpc(ref, { data: [ 'request_password_reset', [ email ] ] })
  },
  async resetPassword(ref) {
    let { code, password } = ref
    return await this.__rpc(ref, { data: [ 'reset_password', [ code, password ] ] })
  },
  async changeUserNames(ref) {
    let { firstName, lastName } = ref
    return await this.__rpc(ref, { data: [ 'change_user_names', [ firstName, lastName ] ] })
  },
  async getAllUsers(ref) {
    return await this.__rpc(ref, { data: [ 'get_all_users' ] })
  },
  async getAccounts(ref) {
    return await this.__rpc(ref, { data: [ 'get_accounts' ] })
  },
  async acceptInvitation(ref) {
    let { fid } = ref
    return await this.__rpc(ref, { data: [ 'accept_invitation', [ fid ] ] })
  },
  async setSubscriber(ref) {
    let { fid, subscriber } = ref
    return await this.__rpc(ref, { data: [ 'set_subscriber', [ fid, subscriber ] ] })
  },
  async deleteAccess(ref) {
    let { fid } = ref
    return await this.__rpc(ref, { data: [ 'delete_access', [ fid ] ] })
  },
  async getPartsList(ref) {
    return await this.__rpc(ref, { data: [ 'get_parts_list' ] })
  },
  async getProvisioningRecords(ref) {
    let args = { }
    for (const k of ['query', 'before'])
      if (k in ref)
        args[k] = ref[k]
    return await this.__rpc(ref, { data: [ 'get_provisioning_records', args ] })
  },
  async getProvisioningRecordsConnected(ref) {
    let { ids } = ref
    return await this.__rpc(ref, { data: [ 'get_provisioning_records_connected', [ ids ] ] })
  },
  async addProvisioningRecord(ref) {
    let { data } = ref
    return await this.__rpc(ref, { data: [ 'add_provisioning_record', [ data ] ] })
  },
  async getFMS(ref) {
    let args = [ 'get_fms' ]
    if ('fid' in ref) args.push([ ref.fid ])
    return await this.__rpc(ref, { data: args })
  },
  async editFMS(ref) {
    let { fid, edits } = ref
    return await this.__rpc(ref, { data: [ 'edit_fms', [ fid, edits ] ] })
  },
  async schedule(ref) {
    const { uid } = ref,
      args = [ uid ]
    if ('data' in ref) {
      args.push(ref.data)
    }
    return await this.__rpc(ref, { data: [ 'schedule', args ] })
  },
  async getTimezones(ref) {
    return await this.__rpc(ref, { data: [ 'get_timezones' ] })
  },
  async getBillingAccounts(ref) {
    return await this.__rpc(ref, { data: [ 'get_billing_accounts' ] })
  },
  async billingAccount(ref) {
    const args = [ ref.aid ]
    if ('stripeToken' in ref)
      args.push(ref.stripeToken)
    return await this.__rpc(ref, { data: [ 'billing_account', args ] })
  },
  async getFlexReportData(ref) {
    const { rid } = ref
    return await this.__rpc(ref, { data: [ 'get_flex_report_data', [ rid ] ] })
  },
  async __rpc(ref, conf) {
    let data
    if (!ref) ref = {}
    try {
    
      // Make sure api is initialized.
      if (!init) {
        await this.__init(ref)
      }

      // Configure and invoke RPC.
      conf.url = '/api/v2'
      conf.cancelToken = new axios.CancelToken(c => { ref.cancel = c })
      if (!conf.method) conf.method = 'POST'
      if (conf.data) conf.data = JSON.stringify(conf.data)
      data = (await axios(conf)).data

    } catch(e) {
      if (axios.isCancel(e)) {
        throw new Canceled('Request canceled.')
      }
      throw e
    }
    if (data) {
      if ('ex' in data)
        throw new RemoteError(data.ex)
      if ('rv' in data)
        return data.rv
    }
    throw new Error('Remote procedure call response did not include a return value.')
  },
  async __init(ref) {
    if (init)
      throw Error('Tried to re-initialize the api.')
    this.stripePK = (await axios({
      url: '/api/v2',
      cancelToken: new axios.CancelToken(c => { ref.cancel = c }),
    })).data
    init = true
  },
  Canceled,
  RemoteError,
  stripePK: null,
}
